import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Map, FileText, Building, Plane, Users, UsersRound, Briefcase, Check, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useCurrency } from '../contexts/CurrencyContext';
import { services } from '../data/mock';

const iconMap = {
  Map,
  FileText,
  Building,
  Plane,
  Users,
  UsersRound,
  Briefcase
};

const ServicesPage = () => {
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();

  const handleLearnMore = (serviceId) => {
    // Navigate to contact page with service pre-selected
    navigate('/contact', { state: { preSelectedService: serviceId } });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#6C30ED]/10 via-white to-[#00DF98]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Our
            <span className="block bg-gradient-to-r from-[#FE4B79] to-[#6C30ED] bg-clip-text text-transparent">
              Travel Services
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-12">
            From initial planning to your safe return, we provide comprehensive travel services 
            designed to make your journey seamless, memorable, and perfectly tailored to your needs.
          </p>
          
          {/* Service highlights */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-[#6C30ED] mb-2">100%</div>
              <p className="text-gray-600">Customized</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[#00DF98] mb-2">24/7</div>
              <p className="text-gray-600">Support</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[#4E8EFF] mb-2">15+</div>
              <p className="text-gray-600">Years Experience</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[#FE4B79] mb-2">5000+</div>
              <p className="text-gray-600">Happy Clients</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Comprehensive Travel Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every service is designed with your comfort, safety, and satisfaction in mind, 
              ensuring a hassle-free travel experience from start to finish.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card 
                  key={service.id}
                  className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg hover:-translate-y-1 bg-gradient-to-br from-white to-gray-50/50 overflow-hidden"
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center">
                        <div className="w-16 h-16 bg-gradient-to-br from-[#6C30ED]/10 to-[#4E8EFF]/10 rounded-2xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                          <IconComponent size={28} className="text-[#6C30ED]" />
                        </div>
                        <div>
                          <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-[#6C30ED] transition-colors">
                            {service.title}
                          </CardTitle>
                          <Badge className="mt-2 bg-[#00DF98]/20 text-[#00DF98] border-[#00DF98]/30">
                            {service.priceINR > 0 ? formatPrice(service.priceINR) : service.pricing}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-0">
                    <p className="text-gray-600 leading-relaxed mb-6">
                      {service.detailedDescription}
                    </p>
                    
                    {/* Features list */}
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3">What's Included:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-600">
                            <Check size={16} className="text-[#00DF98] mr-3 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <Button 
                      onClick={() => handleLearnMore(service.id)}
                      className="w-full bg-gradient-to-r from-[#6C30ED] to-[#4E8EFF] hover:from-[#5c28d4] hover:to-[#3d7eff] text-white font-semibold group/btn"
                    >
                      Learn More
                      <ArrowRight size={16} className="ml-2 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              How We Work
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our streamlined process ensures every detail of your journey is perfectly planned and executed.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Consultation',
                description: 'We understand your travel dreams, preferences, and requirements through detailed consultation.'
              },
              {
                step: '02',
                title: 'Planning',
                description: 'Our experts craft a personalized itinerary tailored to your interests and budget.'
              },
              {
                step: '03',
                title: 'Booking',
                description: 'We handle all bookings, reservations, and documentation with attention to detail.'
              },
              {
                step: '04',
                title: 'Support',
                description: '24/7 support throughout your journey ensures a worry-free travel experience.'
              }
            ].map((item, index) => (
              <Card key={index} className="text-center p-6 shadow-lg border-0 bg-white">
                <div className="w-16 h-16 bg-gradient-to-br from-[#6C30ED] to-[#4E8EFF] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* B2B Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Business Travel Solutions
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Specialized services for travel agents, corporate groups, and business clients. 
                Our B2B solutions include white-label services, group bookings, and dedicated account management.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  'White-label travel packages for agents',
                  'Corporate group travel management',
                  'Dedicated business account managers',
                  'Competitive wholesale pricing',
                  'Custom branding and marketing materials',
                  'Priority booking and support'
                ].map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <Star size={16} className="text-[#00DF98] mr-3 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
              
              <Button 
                size="lg"
                className="bg-gradient-to-r from-[#FE4B79] to-[#6C30ED] hover:from-[#fe3667] hover:to-[#5c28d4] text-white font-semibold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
              >
                Become a Partner
              </Button>
            </div>
            
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=2070"
                alt="Business team planning"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#6C30ED]/20 to-transparent rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology & Innovation */}
      <section className="py-20 bg-gradient-to-r from-[#6C30ED]/5 via-white to-[#4E8EFF]/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Technology-Driven Excellence
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We leverage cutting-edge technology to enhance your travel experience and streamline our services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Digital Itineraries',
                description: 'Access your complete travel itinerary, documents, and real-time updates through our digital platform.',
                icon: '📱'
              },
              {
                title: 'Real-Time Communication',
                description: 'Stay connected with your travel advisor and support team throughout your journey.',
                icon: '💬'
              },
              {
                title: 'Smart Recommendations',
                description: 'AI-powered suggestions based on your preferences, past travels, and current trends.',
                icon: '🤖'
              }
            ].map((feature, index) => (
              <Card key={index} className="text-center p-8 shadow-lg border-0 bg-white">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Ready to Experience Our Services?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Let us handle the details while you focus on creating unforgettable memories. 
            Start planning your perfect journey today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-[#6C30ED] to-[#4E8EFF] hover:from-[#5c28d4] hover:to-[#3d7eff] text-white font-semibold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
            >
              Start Planning
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-[#6C30ED] text-[#6C30ED] hover:bg-[#6C30ED] hover:text-white font-semibold px-8 py-4 rounded-full"
            >
              Download Brochure
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;